// Thin fetch wrapper for the Dryvn Platform API (Node/Express on Cloud Run).
// Attaches Firebase Auth ID token as Bearer. JSON in / JSON out.

import { getIdToken } from "./firebase";

// Ownership diff: default to SAME-ORIGIN ('' → the Firebase Hosting
// /api/** rewrite reaches Cloud Run) instead of localhost — the Lovable
// dev default would break the hosted app when the env var is unset.
// Local dev gets the API via the vite proxy on /api.
const BASE = (import.meta.env.VITE_DRYVN_API_BASE as string | undefined) || "";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

type Options = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  // Skip auth for public endpoints (rare).
  anonymous?: boolean;
};

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const url = `${BASE}${path.startsWith("/") ? path : `/${path}`}`;
  const headers: Record<string, string> = {
    accept: "application/json",
  };
  if (opts.body !== undefined) headers["content-type"] = "application/json";
  if (!opts.anonymous) {
    const token = await getIdToken().catch(() => null);
    if (token) headers.authorization = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  const text = await res.text();
  const parsed = text ? safeJson(text) : null;

  if (!res.ok) {
    const message =
      (parsed && typeof parsed === "object" && "message" in parsed
        ? String((parsed as { message: unknown }).message)
        : res.statusText) || `HTTP ${res.status}`;
    throw new ApiError(res.status, message, parsed);
  }

  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// Convenience helpers scoped to /api/life/*.
export const life = {
  get: <T>(p: string, signal?: AbortSignal) =>
    api<T>(`/api/life/${p.replace(/^\//, "")}`, { signal }),
  post: <T>(p: string, body?: unknown) =>
    api<T>(`/api/life/${p.replace(/^\//, "")}`, { method: "POST", body }),
  put: <T>(p: string, body?: unknown) =>
    api<T>(`/api/life/${p.replace(/^\//, "")}`, { method: "PUT", body }),
  patch: <T>(p: string, body?: unknown) =>
    api<T>(`/api/life/${p.replace(/^\//, "")}`, { method: "PATCH", body }),
  del: <T>(p: string) => api<T>(`/api/life/${p.replace(/^\//, "")}`, { method: "DELETE" }),
};
